import {
  createErrorResponse,
  createSuccessResponse,
  ServiceResponse,
} from "@/services/core/service-registry";
import { typedSupabase } from "@/lib/typed-supabase";
import { generateAttachmentFileName } from "@/types/attachment";
import { logger } from "@/lib/logger";

export interface ChatAttachment {
  id: string;
  conversation_id: string;
  message_id?: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  display_name?: string;
  public_url: string;
  created_at: string;
}

export interface UploadChatAttachmentRequest {
  file: File;
  conversation_id: string;
  message_id?: string;
  display_name?: string;
}

/**
 * Chat Attachment Service
 *
 * Uploads Cortex / AI-chat files to the shared `task-attachments` bucket under a
 * chat-specific path. Does **not** write `task_attachments` rows — that table is
 * FK'd to `todos` and cannot store conversation IDs.
 *
 * Storage RLS requires the first path segment to equal `auth.uid()`.
 */
export class ChatAttachmentService {
  private readonly bucketName = "task-attachments";
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB

  private readonly supportedTypes = [
    // Images
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
    // Documents
    "application/pdf",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    // Audio/Video
    "audio/mpeg",
    "audio/wav",
    "audio/ogg",
    "video/mp4",
    "video/webm",
    "video/ogg",
    // Archives
    "application/zip",
    "application/x-rar-compressed",
    "application/x-7z-compressed",
  ];

  /**
   * Upload a file for a chat conversation (storage only — no todos FK).
   */
  async uploadChatAttachment(
    request: UploadChatAttachmentRequest
  ): Promise<ServiceResponse<ChatAttachment>> {
    const { file, conversation_id, message_id, display_name } = request;

    try {
      this.validateFile(file);

      const {
        data: { user },
        error: authError,
      } = await typedSupabase.auth.getUser();
      if (authError || !user) {
        return createErrorResponse("User not authenticated");
      }

      const sanitizedFileName = generateAttachmentFileName(file.name);
      // RLS: first folder must be auth.uid() (not `users/{uid}/...`)
      const safeConversationId = conversation_id.replace(
        /[^a-zA-Z0-9._-]/g,
        "_"
      );
      const filePath = `${user.id}/chat/${safeConversationId}/${sanitizedFileName}`;

      const { error: uploadError } = await typedSupabase.storage
        .from(this.bucketName)
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) {
        logger.error("Chat attachment storage upload failed", {
          error: uploadError,
          filePath,
          conversationId: conversation_id,
        });
        return createErrorResponse(uploadError.message || "Upload failed");
      }

      const { data: urlData } = typedSupabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath);

      const chatAttachment: ChatAttachment = {
        id: crypto.randomUUID(),
        conversation_id,
        message_id,
        user_id: user.id,
        file_name: sanitizedFileName,
        file_path: filePath,
        file_type: file.type,
        file_size: file.size,
        display_name: display_name || file.name,
        public_url: urlData.publicUrl,
        created_at: new Date().toISOString(),
      };

      return createSuccessResponse(chatAttachment);
    } catch (error) {
      logger.error("Upload chat attachment error:", { error });
      return createErrorResponse(
        error instanceof Error ? error.message : "File validation failed"
      );
    }
  }

  /**
   * List files previously uploaded for a conversation folder.
   */
  async getConversationAttachments(
    conversationId: string
  ): Promise<ChatAttachment[]> {
    try {
      const {
        data: { user },
        error: authError,
      } = await typedSupabase.auth.getUser();
      if (authError || !user) {
        return [];
      }

      const safeConversationId = conversationId.replace(
        /[^a-zA-Z0-9._-]/g,
        "_"
      );
      const folder = `${user.id}/chat/${safeConversationId}`;

      const { data: objects, error } = await typedSupabase.storage
        .from(this.bucketName)
        .list(folder);

      if (error || !objects) {
        if (error) {
          logger.error("List chat attachments error:", { error });
        }
        return [];
      }

      return objects
        .filter((obj) => obj.name && !obj.name.endsWith("/"))
        .map((obj) => {
          const filePath = `${folder}/${obj.name}`;
          const { data: urlData } = typedSupabase.storage
            .from(this.bucketName)
            .getPublicUrl(filePath);

          return {
            id: obj.id || filePath,
            conversation_id: conversationId,
            user_id: user.id,
            file_name: obj.name,
            file_path: filePath,
            file_type: obj.metadata?.mimetype || "application/octet-stream",
            file_size: obj.metadata?.size ?? 0,
            display_name: obj.name,
            public_url: urlData.publicUrl,
            created_at: obj.created_at || new Date().toISOString(),
          };
        });
    } catch (error) {
      logger.error("Get conversation attachments error:", { error });
      return [];
    }
  }

  /**
   * Delete a chat attachment by storage path (preferred) or legacy attachment id.
   */
  async deleteAttachment(filePathOrId: string): Promise<void> {
    try {
      // Chat uploads store the full storage path; treat slash-containing values as paths.
      if (filePathOrId.includes("/")) {
        const { error } = await typedSupabase.storage
          .from(this.bucketName)
          .remove([filePathOrId]);
        if (error) {
          throw error;
        }
        return;
      }

      logger.warn(
        "Chat attachment delete by id is unsupported without a DB row; pass file_path",
        { id: filePathOrId }
      );
    } catch (error) {
      logger.error("Delete chat attachment error:", { error });
      throw error;
    }
  }

  private validateFile(file: File): void {
    if (file.size > this.maxFileSize) {
      throw new Error(
        `File size exceeds limit of ${Math.round(this.maxFileSize / 1024 / 1024)}MB`
      );
    }

    if (!this.supportedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not supported`);
    }

    if (!file.name || file.name.trim() === "") {
      throw new Error("File must have a valid name");
    }
  }

  /**
   * Process clipboard paste for image uploads
   */
  async handleClipboardPaste(event: ClipboardEvent): Promise<File | undefined> {
    const items = event.clipboardData?.items;
    if (!items) {
      return undefined;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") === 0) {
        const blob = item.getAsFile();
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          const extension = item.type.split("/")[1] || "png";
          const fileName = `clipboard-image-${timestamp}.${extension}`;

          return new File([blob], fileName, { type: item.type });
        }
      }
    }

    return undefined;
  }

  getFileCategory(
    fileType: string
  ): "image" | "document" | "audio" | "video" | "archive" | "other" {
    if (fileType.startsWith("image/")) {
      return "image";
    }
    if (fileType.startsWith("audio/")) {
      return "audio";
    }
    if (fileType.startsWith("video/")) {
      return "video";
    }
    if (
      fileType.includes("pdf") ||
      fileType.includes("document") ||
      fileType.includes("text") ||
      fileType.includes("sheet") ||
      fileType.includes("presentation")
    ) {
      return "document";
    }
    if (
      fileType.includes("zip") ||
      fileType.includes("rar") ||
      fileType.includes("7z")
    ) {
      return "archive";
    }
    return "other";
  }
}

export const chatAttachmentService = new ChatAttachmentService();
